import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { DragEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import Masonry from "@mui/lab/Masonry";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import StreamState from "../components/StreamState";
import { useAuth } from "../context/AuthContext";
import { useStreamsState } from "../context/StreamsStateContext";
import { isRecastOnlyMode } from "../utils/streamOutputs";

type MosaicStream = {
  id: string;
  sensor: string;
  platformName?: string;
  active?: boolean;
  mode?: string | null;
};

type StreamsResponse = {
  streams?: MosaicStream[];
  error?: string;
};

type MosaicSession = {
  tabs: string[];
  streamTabs: Record<string, number>;
  activeTab: number;
  streamOrder?: Record<string, string[]>;
};

type PreviewUiState = "loading" | "error";

const DEFAULT_REFRESH_INTERVAL_MS = 2000;
const FAST_REFRESH_INTERVAL_MS = 1000;
const HIDDEN_REFRESH_INTERVAL_MS = 10000;
const PREVIEW_FETCH_CONCURRENCY = 8;
const PREVIEW_STEADY_SLOT_MIN_MS = 100;
const PREVIEW_RETRY_BASE_MS = 1000;
const PREVIEW_RETRY_MAX_MS = 15000;
const PREVIEW_ERROR_DISPLAY_FAILURE_THRESHOLD = 3;
const MOSAIC_STORAGE_KEY = "skyview.mosaic.local";
const MOSAIC_ACTIVE_TAB_KEY = "skyview.mosaic.activeTab";

const hasStoppedMessage = (message: string | null | undefined): boolean =>
  typeof message === "string" && message.trim().toLowerCase().includes("stopped");

const warmImageObjectUrl = async (objectUrl: string, signal: AbortSignal): Promise<void> => {
  await new Promise<void>((resolve, reject) => {
    const image = new Image();
    const finish = (error?: Error) => {
      clearTimeout(timer);
      signal.removeEventListener("abort", abort);
      image.onload = null;
      image.onerror = null;
      image.removeAttribute("src");
      if (error) reject(error);
      else resolve();
    };
    const abort = () => finish(new DOMException("Preview cancelled", "AbortError"));
    const timer = window.setTimeout(() => finish(new Error("preview image decode timed out")), 10000);
    signal.addEventListener("abort", abort, { once: true });
    if (signal.aborted) {
      abort();
      return;
    }
    image.onload = () => finish();
    image.onerror = () => finish(new Error("unable to decode preview image"));
    image.src = objectUrl;
  });
};

const loadSession = (): MosaicSession | null => {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    const raw = localStorage.getItem(MOSAIC_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as Partial<MosaicSession> | null;
    if (!parsed || !Array.isArray(parsed.tabs) || typeof parsed.streamTabs !== "object" || parsed.streamTabs === null) {
      return null;
    }
    const activeTab =
      typeof parsed.activeTab === "number"
        ? parsed.activeTab
        : Number(localStorage.getItem(MOSAIC_ACTIVE_TAB_KEY) ?? "0");
    return {
      tabs: parsed.tabs.filter((tab) => typeof tab === "string"),
      streamTabs: parsed.streamTabs as Record<string, number>,
      activeTab,
      streamOrder:
        typeof parsed.streamOrder === "object" && parsed.streamOrder
          ? (parsed.streamOrder as Record<string, string[]>)
          : undefined
    };
  } catch {
    return null;
  }
};

const MosaicPage = () => {
  const { authorizedFetch, resolveApiPath } = useAuth();
  const { getStreamState, getStreamStateMessage } = useStreamsState();
  const navigate = useNavigate();
  const [refreshKey, setRefreshKey] = useState(0);
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});
  const ownedPreviewUrlsRef = useRef(new Set<string>());
  const previewETagRef = useRef<Record<string, string>>({});
  const previewCapturedAtRef = useRef<Record<string, number>>({});
  const releasePreviewUrl = useCallback((url: string) => {
    URL.revokeObjectURL(url);
    ownedPreviewUrlsRef.current.delete(url);
  }, []);
  const [previewStates, setPreviewStates] = useState<Record<string, PreviewUiState>>({});
  const previewInFlightRef = useRef<Record<string, AbortController>>({});
  const previewRetryAtRef = useRef<Record<string, number>>({});
  const previewNextAtRef = useRef<Record<string, number>>({});
  const previewFailureCountRef = useRef<Record<string, number>>({});
  const previewLastModifiedRef = useRef<Record<string, string>>({});
  const previewInitializedRef = useRef<Record<string, boolean>>({});
  const steadyCursorRef = useRef(0);
  const activeStreamIdsRef = useRef<Set<string>>(new Set());
  const isUnmountedRef = useRef(false);
  const [isPageHidden, setIsPageHidden] = useState(() => (typeof document !== "undefined" ? document.hidden : false));
  const [activeTab, setActiveTab] = useState(() => {
    const session = loadSession();
    if (session?.tabs?.length && session.activeTab >= 0 && session.activeTab < session.tabs.length) {
      return session.activeTab;
    }
    return 0;
  });
  const [tabs, setTabs] = useState<string[]>(() => {
    const session = loadSession();
    if (session?.tabs?.length) {
      return session.tabs;
    }
    return ["Mosaic"];
  });
  const [nameDialogOpen, setNameDialogOpen] = useState(false);
  const [nameDialogMode, setNameDialogMode] = useState<"add" | "edit">("add");
  const [nameDraft, setNameDraft] = useState("");
  const [checkedStreamIds, setCheckedStreamIds] = useState<string[]>([]);
  const [dialogStreamTabs, setDialogStreamTabs] = useState<Record<string, number> | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [recastOnlyDialogStreamName, setRecastOnlyDialogStreamName] = useState<string | null>(null);
  const [selectedStreamId, setSelectedStreamId] = useState<string | null>(null);
  const [streamTabs, setStreamTabs] = useState<Record<string, number>>(() => loadSession()?.streamTabs ?? {});
  const [streamOrder, setStreamOrder] = useState<Record<string, string[]>>(() => loadSession()?.streamOrder ?? {});
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const isSm = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isMd = useMediaQuery(theme.breakpoints.between("md", "lg"));

  const columns = useMemo(() => {
    if (isXs) {
      return 1;
    }
    if (isSm) {
      return 2;
    }
    if (isMd) {
      return 3;
    }
    return 4;
  }, [isXs, isSm, isMd]);

  const { data, isLoading, isError, error } = useQuery<MosaicStream[]>({
    queryKey: ["mosaic-streams"],
    queryFn: async () => {
      const response = await authorizedFetch("/api/streams");
      const payload = (await response.json().catch(() => null)) as StreamsResponse | null;
      if (!response.ok) {
        throw new Error(payload?.error ?? "Unable to load streams");
      }
      const streams = Array.isArray(payload?.streams) ? payload.streams : [];
      return streams.filter((stream) => stream && stream.active !== false);
    }
  });

  const streams = data ?? [];
  const streamIds = useMemo(() => new Set(streams.map((stream) => stream.id)), [streams]);
  const availableStreams = useMemo(
    () => streams.filter((stream) => streamTabs[stream.id] !== activeTab),
    [streams, streamTabs, activeTab]
  );
  const assignedStreams = useMemo(
    () => streams.filter((stream) => streamTabs[stream.id] === activeTab),
    [streams, streamTabs, activeTab]
  );
  const dialogAvailableStreams = useMemo(() => {
    const tabsState = dialogStreamTabs ?? streamTabs;
    return streams.filter((stream) => tabsState[stream.id] !== activeTab);
  }, [streams, dialogStreamTabs, streamTabs, activeTab]);
  const dialogAssignedStreams = useMemo(() => {
    const tabsState = dialogStreamTabs ?? streamTabs;
    return streams.filter((stream) => tabsState[stream.id] === activeTab);
  }, [streams, dialogStreamTabs, streamTabs, activeTab]);
  const streamsForActiveTab = useMemo(() => {
    const order = streamOrder[String(activeTab)] ?? [];
    if (order.length === 0) {
      return assignedStreams;
    }
    const assignedMap = new Map(assignedStreams.map((stream) => [stream.id, stream]));
    const ordered: MosaicStream[] = [];
    order.forEach((id) => {
      const stream = assignedMap.get(id);
      if (stream) {
        ordered.push(stream);
        assignedMap.delete(id);
      }
    });
    assignedMap.forEach((stream) => ordered.push(stream));
    return ordered;
  }, [assignedStreams, activeTab, streamOrder]);
  const refreshInterval =
    streamsForActiveTab.length > 0 && streamsForActiveTab.length < 10
      ? FAST_REFRESH_INTERVAL_MS
      : DEFAULT_REFRESH_INTERVAL_MS;
  const effectiveRefreshInterval = isPageHidden ? Math.max(HIDDEN_REFRESH_INTERVAL_MS, refreshInterval) : refreshInterval;
  const steadySlotIntervalMs =
    streamsForActiveTab.length > 0
      ? Math.max(PREVIEW_STEADY_SLOT_MIN_MS, Math.floor(effectiveRefreshInterval / streamsForActiveTab.length))
      : effectiveRefreshInterval;

  useEffect(() => {
    activeStreamIdsRef.current = new Set(streamsForActiveTab.map((stream) => stream.id));
  }, [streamsForActiveTab]);

  useEffect(() => {
    const onVisibilityChange = () => {
      setIsPageHidden(document.hidden);
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (streamsForActiveTab.length === 0) {
      return;
    }
    const timer = window.setInterval(() => {
      setRefreshKey((value) => value + 1);
    }, steadySlotIntervalMs);
    return () => window.clearInterval(timer);
  }, [steadySlotIntervalMs, streamsForActiveTab.length]);

  useEffect(() => {
    if (streamsForActiveTab.length === 0) {
      return;
    }
    let cancelled = false;

    const fetchPreview = async (stream: MosaicStream) => {
      const controller = new AbortController();
      previewInFlightRef.current[stream.id] = controller;
      setPreviewStates((prev) => ({ ...prev, [stream.id]: "loading" }));

      try {
        const previewUrl = resolveApiPath(`/api/streams/${stream.id}/preview.jpg`);
        const ifModifiedSince = previewLastModifiedRef.current[stream.id];
        const etag = previewETagRef.current[stream.id];
        const response = await authorizedFetch(previewUrl, {
          signal: controller.signal,
          headers: etag ? { "If-None-Match": etag } : ifModifiedSince ? { "If-Modified-Since": ifModifiedSince } : undefined
        });
        if (controller.signal.aborted || isUnmountedRef.current || !activeStreamIdsRef.current.has(stream.id)) return;
        if (response.status === 304) {
          previewInitializedRef.current[stream.id] = true;
          previewFailureCountRef.current[stream.id] = 0;
          previewRetryAtRef.current[stream.id] = 0;
          previewNextAtRef.current[stream.id] = Date.now() + effectiveRefreshInterval;
          setPreviewStates((prev) => {
            if (!(stream.id in prev)) {
              return prev;
            }
            const next = { ...prev };
            delete next[stream.id];
            return next;
          });
          return;
        }
        if (!response.ok) {
          const error = new Error("preview unavailable") as Error & { status?: number };
          error.status = response.status;
          throw error;
        }
        const lastModified = response.headers.get("Last-Modified");
        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        ownedPreviewUrlsRef.current.add(objectUrl);
        try {
          await warmImageObjectUrl(objectUrl, controller.signal);
        } catch {
          releasePreviewUrl(objectUrl);
          throw new Error("preview decode failed");
        }
        if (controller.signal.aborted || isUnmountedRef.current || !activeStreamIdsRef.current.has(stream.id)) {
          releasePreviewUrl(objectUrl);
          return;
        }
        if (lastModified) {
          previewLastModifiedRef.current[stream.id] = lastModified;
          previewCapturedAtRef.current[stream.id] = Date.parse(lastModified);
        }
        const newETag = response.headers.get("ETag");
        if (newETag) previewETagRef.current[stream.id] = newETag;
        previewFailureCountRef.current[stream.id] = 0;
        previewRetryAtRef.current[stream.id] = 0;
        previewInitializedRef.current[stream.id] = true;
        previewNextAtRef.current[stream.id] = Date.now() + effectiveRefreshInterval;
        setPreviewStates((prev) => {
          if (!(stream.id in prev)) {
            return prev;
          }
          const next = { ...prev };
          delete next[stream.id];
          return next;
        });
        setPreviewUrls((prev) => {
          const existing = prev[stream.id];
          if (existing) {
            releasePreviewUrl(existing);
          }
          return { ...prev, [stream.id]: objectUrl };
        });
      } catch (error) {
        if (controller.signal.aborted || isUnmountedRef.current || !activeStreamIdsRef.current.has(stream.id)) return;
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
        const failureCount = (previewFailureCountRef.current[stream.id] ?? 0) + 1;
        previewFailureCountRef.current[stream.id] = failureCount;
        const status =
          typeof error === "object" && error !== null && "status" in error
            ? Number((error as { status?: number }).status)
            : undefined;
        // Keep transient failures in loading/retrying state; surface hard errors only after repeated failures.
        const isNotReady = status === 404;
        const shouldShowError = !isNotReady && failureCount >= PREVIEW_ERROR_DISPLAY_FAILURE_THRESHOLD;
        const nextState: PreviewUiState = shouldShowError ? "error" : "loading";
        setPreviewStates((prev) => ({ ...prev, [stream.id]: nextState }));
        const retryDelay = Math.min(PREVIEW_RETRY_MAX_MS, PREVIEW_RETRY_BASE_MS * 2 ** Math.min(failureCount - 1, 4));
        const nextAttemptAt = Date.now() + retryDelay;
        previewRetryAtRef.current[stream.id] = nextAttemptAt;
        previewNextAtRef.current[stream.id] = nextAttemptAt;
      } finally {
        if (previewInFlightRef.current[stream.id] === controller) delete previewInFlightRef.current[stream.id];
      }
    };

    const isStoppedLike = (streamId: string) => {
      const state = getStreamState(streamId);
      return state === "offline" || state === "unknown" || hasStoppedMessage(getStreamStateMessage(streamId));
    };

    const now = Date.now();
    const candidates = streamsForActiveTab.filter((stream) => {
      if (getStreamState(stream.id) === "timeout" || isStoppedLike(stream.id)) {
        return false;
      }
      if (previewInFlightRef.current[stream.id]) {
        return false;
      }
      const retryAt = previewRetryAtRef.current[stream.id] ?? 0;
      if (retryAt > now) {
        return false;
      }
      const nextAt = previewNextAtRef.current[stream.id] ?? 0;
      return nextAt <= now;
    });

    if (candidates.length === 0) {
      return;
    }

    const bootstrapQueue = candidates.filter((stream) => !previewInitializedRef.current[stream.id]);
    if (bootstrapQueue.length > 0) {
      const queue = [...bootstrapQueue];
      const workerCount = Math.min(PREVIEW_FETCH_CONCURRENCY, queue.length);
      const runWorker = async () => {
        while (!cancelled) {
          const stream = queue.shift();
          if (!stream) {
            return;
          }
          await fetchPreview(stream);
        }
      };
      for (let index = 0; index < workerCount; index += 1) {
        void runWorker();
      }
      return () => {
        cancelled = true;
      };
    }

    const candidateIDs = new Set(candidates.map((stream) => stream.id));
    const ordered = streamsForActiveTab.filter((stream) => candidateIDs.has(stream.id));
    if (ordered.length === 0) {
      return;
    }
    const startIndex = steadyCursorRef.current % ordered.length;
    let picked: MosaicStream | null = null;
    for (let offset = 0; offset < ordered.length; offset += 1) {
      const stream = ordered[(startIndex + offset) % ordered.length];
      if (!stream) {
        continue;
      }
      picked = stream;
      steadyCursorRef.current = (startIndex + offset + 1) % ordered.length;
      break;
    }
    if (picked) {
      void fetchPreview(picked);
    }

    return () => {
      cancelled = true;
    };
  }, [authorizedFetch, effectiveRefreshInterval, getStreamState, getStreamStateMessage, resolveApiPath, refreshKey, streamsForActiveTab]);

  useEffect(() => {
    const activeStreamIds = new Set(streamsForActiveTab.map((stream) => stream.id));
    // Validators must never outlive their image, even when no request is active.
    Object.keys(previewInitializedRef.current).forEach((id) => {
      if (!activeStreamIds.has(id)) {
        delete previewInitializedRef.current[id];
        delete previewLastModifiedRef.current[id];
        delete previewETagRef.current[id];
        delete previewCapturedAtRef.current[id];
        delete previewRetryAtRef.current[id];
        delete previewNextAtRef.current[id];
        delete previewFailureCountRef.current[id];
      }
    });
    Object.keys(previewInFlightRef.current).forEach((id) => {
      if (!activeStreamIds.has(id)) {
        previewInFlightRef.current[id].abort();
        delete previewInFlightRef.current[id];
        delete previewRetryAtRef.current[id];
        delete previewNextAtRef.current[id];
        delete previewFailureCountRef.current[id];
        delete previewLastModifiedRef.current[id];
        delete previewInitializedRef.current[id];
      }
    });
    setPreviewStates((prev) => {
      let changed = false;
      const next = { ...prev };
      Object.keys(next).forEach((id) => {
        if (!activeStreamIds.has(id)) {
          delete next[id];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    setPreviewUrls((prev) => {
      let changed = false;
      const next = { ...prev };
      Object.keys(next).forEach((id) => {
        if (!activeStreamIds.has(id)) {
          releasePreviewUrl(next[id]);
          delete next[id];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [streamsForActiveTab]);

  useEffect(() => {
    Object.keys(previewInFlightRef.current).forEach((id) => {
      if (!streamIds.has(id)) {
        previewInFlightRef.current[id].abort();
        delete previewInFlightRef.current[id];
        delete previewRetryAtRef.current[id];
        delete previewNextAtRef.current[id];
        delete previewFailureCountRef.current[id];
        delete previewLastModifiedRef.current[id];
        delete previewInitializedRef.current[id];
      }
    });
    setPreviewStates((prev) => {
      let changed = false;
      const next = { ...prev };
      Object.keys(next).forEach((id) => {
        if (!streamIds.has(id)) {
          delete next[id];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    setPreviewUrls((prev) => {
      let changed = false;
      const next = { ...prev };
      Object.keys(next).forEach((id) => {
        if (!streamIds.has(id)) {
          releasePreviewUrl(next[id]);
          delete next[id];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [streamIds]);

  useEffect(() => {
    isUnmountedRef.current = false;
    return () => {
      isUnmountedRef.current = true;
      Object.values(previewInFlightRef.current).forEach((controller) => controller.abort());
      previewInFlightRef.current = {};
      previewRetryAtRef.current = {};
      previewNextAtRef.current = {};
      previewFailureCountRef.current = {};
      previewLastModifiedRef.current = {};
      previewInitializedRef.current = {};
      previewETagRef.current = {};
      previewCapturedAtRef.current = {};
      ownedPreviewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      ownedPreviewUrlsRef.current.clear();
      steadyCursorRef.current = 0;
    };
  }, []);

  useEffect(() => {
    if (isLoading) {
      return;
    }
    setStreamTabs((prev) => {
      let changed = false;
      const next: Record<string, number> = {};
      Object.entries(prev).forEach(([streamId, tabIndex]) => {
        if (!streamIds.has(streamId)) {
          changed = true;
          return;
        }
        if (tabIndex >= tabs.length) {
          next[streamId] = 0;
          changed = true;
          return;
        }
        next[streamId] = tabIndex;
      });
      return changed ? next : prev;
    });
  }, [isLoading, streamIds, tabs.length]);

  useEffect(() => {
    if (isLoading) {
      return;
    }
    setStreamOrder((prev) => {
      let changed = false;
      const next: Record<string, string[]> = {};
      Object.entries(prev).forEach(([tabKey, order]) => {
        const tabIndex = Number(tabKey);
        if (Number.isNaN(tabIndex) || tabIndex < 0 || tabIndex >= tabs.length) {
          changed = true;
          return;
        }
        const filtered = order.filter((id) => streamIds.has(id));
        if (filtered.length !== order.length) {
          changed = true;
        }
        next[String(tabIndex)] = filtered;
      });
      return changed ? next : prev;
    });
  }, [isLoading, streamIds, tabs.length]);

  useEffect(() => {
    if (tabs.length === 0) {
      if (activeTab !== 0) {
        setActiveTab(0);
      }
      return;
    }
    if (activeTab < 0 || activeTab >= tabs.length) {
      setActiveTab(Math.max(0, tabs.length - 1));
    }
  }, [activeTab, tabs]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    try {
      const payload: MosaicSession = {
        tabs,
        streamTabs,
        activeTab,
        streamOrder
      };
      localStorage.setItem(MOSAIC_STORAGE_KEY, JSON.stringify(payload));
      localStorage.setItem(MOSAIC_ACTIVE_TAB_KEY, String(activeTab));
    } catch {
      // Ignore session storage failures.
    }
  }, [tabs, streamTabs, activeTab, streamOrder]);

  useEffect(() => {
    if (!nameDialogOpen || nameDialogMode !== "edit") {
      return;
    }
    setCheckedStreamIds([]);
    setDialogStreamTabs((prev) => prev ?? { ...streamTabs });
  }, [activeTab, nameDialogMode, nameDialogOpen]);

  const handleOpenAdd = () => {
    setNameDialogMode("add");
    setNameDraft("");
    setCheckedStreamIds([]);
    setDialogStreamTabs(null);
    setNameDialogOpen(true);
  };

  const handleOpenEdit = () => {
    setNameDialogMode("edit");
    setNameDraft(tabs[activeTab] ?? "");
    setCheckedStreamIds([]);
    setDialogStreamTabs({ ...streamTabs });
    setNameDialogOpen(true);
  };

  const handleCloseNameDialog = () => {
    setNameDialogOpen(false);
    setCheckedStreamIds([]);
    setDialogStreamTabs(null);
  };

  const handleSaveName = () => {
    const trimmed = nameDraft.trim();
    if (!trimmed) {
      return;
    }
    if (nameDialogMode === "add") {
      setTabs((prev) => {
        setActiveTab(prev.length);
        return [...prev, trimmed];
      });
    } else {
      setTabs((prev) => {
        const next = [...prev];
        next[activeTab] = trimmed;
        return next;
      });
      if (dialogStreamTabs) {
        setStreamTabs(dialogStreamTabs);
        setStreamOrder((prev) => {
          const assignedByTab: Record<string, Set<string>> = {};
          Object.entries(dialogStreamTabs).forEach(([streamId, tabIndex]) => {
            const key = String(tabIndex);
            if (!assignedByTab[key]) {
              assignedByTab[key] = new Set();
            }
            assignedByTab[key].add(streamId);
          });
          let changed = false;
          const next: Record<string, string[]> = {};
          for (let index = 0; index < tabs.length; index += 1) {
            const key = String(index);
            const assignedSet = assignedByTab[key] ?? new Set<string>();
            const prevOrder = prev[key] ?? [];
            const kept = prevOrder.filter((id) => assignedSet.has(id));
            const missing = Array.from(assignedSet).filter((id) => !kept.includes(id));
            const nextOrder = [...kept, ...missing];
            next[key] = nextOrder;
            if (missing.length > 0 || kept.length !== prevOrder.length || !prev[key]) {
              changed = true;
            }
          }
          return changed ? next : prev;
        });
      }
    }
    setNameDialogOpen(false);
    setCheckedStreamIds([]);
    setDialogStreamTabs(null);
  };

  const handleToggleStream = (streamId: string) => {
    setCheckedStreamIds((prev) => {
      if (prev.includes(streamId)) {
        return prev.filter((id) => id !== streamId);
      }
      return [...prev, streamId];
    });
  };

  const handleMoveToAssigned = () => {
    const availableIds = new Set(dialogAvailableStreams.map((stream) => stream.id));
    const selected = checkedStreamIds.filter((id) => availableIds.has(id));
    if (selected.length === 0) {
      return;
    }
    setDialogStreamTabs((prev) => {
      if (!prev) {
        return prev;
      }
      const next = { ...prev };
      selected.forEach((id) => {
        next[id] = activeTab;
      });
      return next;
    });
    setCheckedStreamIds((prev) => prev.filter((id) => !selected.includes(id)));
  };

  const handleMoveToAvailable = () => {
    const assignedIds = new Set(dialogAssignedStreams.map((stream) => stream.id));
    const selected = checkedStreamIds.filter((id) => assignedIds.has(id));
    if (selected.length === 0) {
      return;
    }
    setDialogStreamTabs((prev) => {
      if (!prev) {
        return prev;
      }
      const next = { ...prev };
      selected.forEach((id) => {
        delete next[id];
      });
      return next;
    });
    setCheckedStreamIds((prev) => prev.filter((id) => !selected.includes(id)));
  };

  const handleOpenDelete = () => {
    setDeleteDialogOpen(true);
  };

  const handleCloseDelete = () => {
    setDeleteDialogOpen(false);
  };

  const handleConfirmDelete = () => {
    setDeleteDialogOpen(false);
    setTabs((prev) => {
      if (prev.length <= 1) {
        return prev;
      }
      const removedIndex = activeTab;
      const next = prev.filter((_, index) => index !== removedIndex);
      setStreamTabs((streamPrev) => {
        const streamNext: Record<string, number> = {};
        Object.entries(streamPrev).forEach(([streamId, tabIndex]) => {
          if (tabIndex === removedIndex) {
            streamNext[streamId] = 0;
          } else if (tabIndex > removedIndex) {
            streamNext[streamId] = tabIndex - 1;
          } else {
            streamNext[streamId] = tabIndex;
          }
        });
        return streamNext;
      });
      setStreamOrder((prevOrder) => {
        const nextOrder: Record<string, string[]> = {};
        Object.entries(prevOrder).forEach(([tabKey, order]) => {
          const tabIndex = Number(tabKey);
          if (Number.isNaN(tabIndex) || tabIndex === removedIndex) {
            return;
          }
          const nextIndex = tabIndex > removedIndex ? tabIndex - 1 : tabIndex;
          nextOrder[String(nextIndex)] = order;
        });
        return nextOrder;
      });
      const nextActive = Math.min(removedIndex, next.length - 1);
      setActiveTab(Math.max(0, nextActive));
      return next;
    });
  };

  const handleOpenPlayer = (stream: MosaicStream) => {
    const streamName = stream.platformName ? `${stream.platformName} / ${stream.sensor}` : stream.sensor;
    if (isRecastOnlyMode(stream.mode)) {
      setRecastOnlyDialogStreamName(streamName);
      return;
    }
    navigate(`/player?streamId=${encodeURIComponent(stream.id)}`);
  };

  const handleStreamDragStart = (event: DragEvent, streamId: string) => {
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("application/x-skyview-stream-id", streamId);
    event.dataTransfer.setData("text/plain", streamId);
  };

  const handleStreamDragOver = (event: DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  };

  const handleStreamDrop = (event: DragEvent, targetId: string) => {
    event.preventDefault();
    const streamId =
      event.dataTransfer.getData("application/x-skyview-stream-id") || event.dataTransfer.getData("text/plain");
    if (!streamId || streamId === targetId) {
      return;
    }
    if (streamTabs[streamId] !== activeTab || streamTabs[targetId] !== activeTab) {
      return;
    }
    setStreamOrder((prev) => {
      const key = String(activeTab);
      const current = prev[key] ?? streamsForActiveTab.map((stream) => stream.id);
      const filtered = current.filter((id) => id !== streamId);
      const targetIndex = filtered.indexOf(targetId);
      if (targetIndex === -1) {
        return prev;
      }
      filtered.splice(targetIndex, 0, streamId);
      return { ...prev, [key]: filtered };
    });
  };

  const handleTabDragOver = (event: DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  };

  const handleTabDrop = (event: DragEvent, targetIndex: number) => {
    event.preventDefault();
    const streamId =
      event.dataTransfer.getData("application/x-skyview-stream-id") || event.dataTransfer.getData("text/plain");
    if (!streamId) {
      return;
    }
    setStreamTabs((prev) => ({ ...prev, [streamId]: targetIndex }));
    setStreamOrder((prev) => {
      const next: Record<string, string[]> = { ...prev };
      Object.keys(next).forEach((key) => {
        next[key] = next[key].filter((id) => id !== streamId);
      });
      const targetKey = String(targetIndex);
      const existing = next[targetKey] ?? [];
      next[targetKey] = [...existing, streamId];
      return next;
    });
  };

  const selectedStreamIdForTab =
    selectedStreamId && streamTabs[selectedStreamId] === activeTab ? selectedStreamId : null;
  const hasSelection = Boolean(selectedStreamIdForTab);
  const dialogStreamsChanged = useMemo(() => {
    if (nameDialogMode !== "edit" || !dialogStreamTabs) {
      return false;
    }
    const keys = Object.keys(streamTabs);
    const dialogKeys = Object.keys(dialogStreamTabs);
    if (keys.length !== dialogKeys.length) {
      return true;
    }
    return keys.some((key) => dialogStreamTabs[key] !== streamTabs[key]);
  }, [dialogStreamTabs, nameDialogMode, streamTabs]);
  const dialogNameChanged = nameDialogMode === "edit" && nameDraft.trim() !== (tabs[activeTab] ?? "");
  const dialogDirty =
    nameDialogMode === "add" ? Boolean(nameDraft.trim()) : dialogNameChanged || dialogStreamsChanged;

  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }
      if (target.closest("[data-stream-card=\"true\"]")) {
        return;
      }
      setSelectedStreamId(null);
    };
    document.addEventListener("click", handleDocumentClick);
    return () => document.removeEventListener("click", handleDocumentClick);
  }, []);

  return (
    <Box sx={{ px: { xs: 1, md: 2 }, py: 2 }}>
      <Stack spacing={0.5} sx={{ mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="h5" fontWeight={600}>
            Mosaic
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Add Mosaic page">
              <span>
                <Button size="small" variant="outlined" onClick={handleOpenAdd} startIcon={<AddIcon />}>
                  Add Page
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Edit page">
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleOpenEdit}
                  startIcon={<EditIcon />}
                  disabled={tabs.length === 0}
                >
                  Edit Streams
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Remove page">
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={handleOpenDelete}
                  startIcon={<DeleteIcon />}
                  disabled={tabs.length <= 1}
                >
                  Remove
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Box>
        <Tabs
          value={activeTab}
          onChange={(_event, value) => setActiveTab(value)}
          textColor="primary"
          indicatorColor="primary"
          sx={{ minHeight: 40 }}
        >
          {tabs.map((label, index) => (
            <Tab
              key={`${label}-${index}`}
              label={label}
              onDragOver={handleTabDragOver}
              onDrop={(event) => handleTabDrop(event, index)}
            />
          ))}
        </Tabs>
      </Stack>
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Typography color="error">
          {error instanceof Error ? error.message : "Unable to load streams."}
        </Typography>
      ) : streamsForActiveTab.length === 0 ? (
        <Typography color="text.secondary">No streams available.</Typography>
      ) : (
        <Masonry
          columns={columns}
          spacing={1}
          sx={{
            "& [data-stream-card=\"true\"]": {
              transition: "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease"
            },
            ...(hasSelection
              ? {
                  "& [data-stream-card=\"true\"][data-selected=\"true\"]": {
                    transform: "scale(1.14)",
                    boxShadow: 8,
                    zIndex: 2
                  },
                  "& [data-stream-card=\"true\"][data-selected=\"false\"]": {
                    transform: "scale(0.94)",
                    boxShadow: 0
                  }
                }
              : {})
          }}
        >
          {streamsForActiveTab.map((stream) => {
            const title = stream.platformName ? `${stream.platformName} / ${stream.sensor}` : stream.sensor;
            const previewUrl = previewUrls[stream.id];
            const previewState = previewStates[stream.id];
            const streamState = getStreamState(stream.id);
            const streamStateMessage = getStreamStateMessage(stream.id);
            const isTimeout = streamState === "timeout";
            const isOffline =
              streamState === "offline" || streamState === "unknown" || hasStoppedMessage(streamStateMessage);
            const isSelected = selectedStreamIdForTab === stream.id;
            return (
              <Box
                key={stream.id}
                data-stream-card="true"
                data-selected={isSelected ? "true" : "false"}
                onDoubleClick={() => handleOpenPlayer(stream)}
                draggable
                onDragStart={(event) => handleStreamDragStart(event, stream.id)}
                onDragOver={handleStreamDragOver}
                onDrop={(event) => handleStreamDrop(event, stream.id)}
                onClick={(event) => {
                  event.stopPropagation();
                  setSelectedStreamId(stream.id);
                }}
                sx={{
                  position: "relative",
                  borderRadius: 1.5,
                  overflow: "hidden",
                  bgcolor: "action.hover",
                  border: "1px solid",
                  borderColor: isSelected ? "primary.main" : "divider",
                  cursor: "pointer",
                  boxShadow: isSelected ? 8 : 1,
                  zIndex: isSelected ? 2 : "auto"
                }}
              >
                {isOffline ? (
                  <Box
                    sx={{
                      width: "100%",
                      aspectRatio: "16 / 9",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "background.default",
                      color: "text.secondary",
                      gap: 0.5
                    }}
                  >
                    <Typography variant="subtitle2">Offline</Typography>
                  </Box>
                ) : isTimeout ? (
                  <Box
                    sx={{
                      width: "100%",
                      aspectRatio: "16 / 9",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "background.default",
                      color: "warning.main",
                      gap: 0.5
                    }}
                  >
                    <Typography variant="subtitle2">Timeout</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Waiting for stream…
                    </Typography>
                  </Box>
                ) : previewUrl ? (
                  <Box
                    component="img"
                    src={previewUrl}
                    alt={title}
                    loading="lazy"
                    sx={{
                      width: "100%",
                      display: "block",
                      aspectRatio: "16 / 9",
                      objectFit: "cover",
                      bgcolor: "background.default"
                    }}
                  />
                ) : previewState === "error" ? (
                  <Box
                    sx={{
                      width: "100%",
                      aspectRatio: "16 / 9",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "background.default",
                      color: "text.secondary",
                      gap: 0.6
                    }}
                  >
                    <Typography variant="body2">Preview unavailable</Typography>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      width: "100%",
                      aspectRatio: "16 / 9",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "background.default",
                      color: "text.secondary",
                      gap: 0.6
                    }}
                  >
                    <CircularProgress size={22} />
                    <Typography variant="body2">Loading preview…</Typography>
                  </Box>
                )}
                <Box
                  sx={{
                    position: "absolute",
                    inset: "auto 0 0 0",
                    px: 1,
                    py: 0.1,
                    bgcolor: "rgba(0, 0, 0, 0.4)",
                    color: "common.white"
                  }}
                >
                  <Typography variant="caption" noWrap sx={{ fontSize: "0.7rem" }}>
                    {title}
                    {previewUrl && !isOffline && !isTimeout && Number.isFinite(previewCapturedAtRef.current[stream.id])
                      ? ` · ${Math.max(0, Math.floor((Date.now() - previewCapturedAtRef.current[stream.id]) / 1000))}s ago`
                      : ""}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Masonry>
      )}
      <Dialog
        open={Boolean(recastOnlyDialogStreamName)}
        onClose={() => setRecastOnlyDialogStreamName(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>⚠️ Cannot Open in Player</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Stream "{recastOnlyDialogStreamName}" is in Recast only mode and cannot be opened in the Player.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecastOnlyDialogStreamName(null)} autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={nameDialogOpen} onClose={handleCloseNameDialog} fullWidth maxWidth="md">
        <DialogTitle>{nameDialogMode === "add" ? "Add Mosaic page" : "Edit page"}</DialogTitle>
        <DialogContent sx={{ pt: 1.5 }}>
          <Stack spacing={1.5} sx={{ mt: 0.5, width: "100%" }}>
            <TextField
              autoFocus
              label="Page name"
              value={nameDraft}
              onChange={(event) => setNameDraft(event.target.value)}
              fullWidth
              margin="dense"
              size="small"
            />
            {nameDialogMode === "edit" ? (
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                alignItems="stretch"
                sx={{ width: "100%" }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Paper variant="outlined" sx={{ width: "100%", height: 240, overflow: "auto" }}>
                    <List dense component="div" role="list" sx={{ py: 0 }}>
                      {dialogAvailableStreams.map((stream) => {
                        const title = stream.platformName ? `${stream.platformName} / ${stream.sensor}` : stream.sensor;
                        const checked = checkedStreamIds.includes(stream.id);
                        return (
                          <ListItemButton
                            key={stream.id}
                            role="listitem"
                            onClick={() => handleToggleStream(stream.id)}
                            dense
                            sx={{ py: 0.25 }}
                          >
                            <ListItemIcon>
                              <Checkbox checked={checked} tabIndex={-1} disableRipple size="small" />
                            </ListItemIcon>
                            <ListItemIcon sx={{ minWidth: 26 }}>
                              <StreamState state={getStreamState(stream.id)} size={14} />
                            </ListItemIcon>
                            <ListItemText primary={title} primaryTypographyProps={{ variant: "body2" }} />
                          </ListItemButton>
                        );
                      })}
                    </List>
                  </Paper>
                </Box>
                <Box sx={{ flex: "0 0 120px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Stack direction="column" spacing={0.75} alignItems="center">
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleMoveToAssigned}
                      disabled={!checkedStreamIds.some((id) => dialogAvailableStreams.some((stream) => stream.id === id))}
                    >
                      Add &gt;&gt;
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleMoveToAvailable}
                      disabled={!checkedStreamIds.some((id) => dialogAssignedStreams.some((stream) => stream.id === id))}
                    >
                      &lt;&lt; Remove
                    </Button>
                  </Stack>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Paper variant="outlined" sx={{ width: "100%", height: 240, overflow: "auto" }}>
                    <List dense component="div" role="list" sx={{ py: 0 }}>
                      {dialogAssignedStreams.map((stream) => {
                        const title = stream.platformName ? `${stream.platformName} / ${stream.sensor}` : stream.sensor;
                        const checked = checkedStreamIds.includes(stream.id);
                        return (
                          <ListItemButton
                            key={stream.id}
                            role="listitem"
                            onClick={() => handleToggleStream(stream.id)}
                            dense
                            sx={{ py: 0.25 }}
                          >
                            <ListItemIcon>
                              <Checkbox checked={checked} tabIndex={-1} disableRipple size="small" />
                            </ListItemIcon>
                            <ListItemIcon sx={{ minWidth: 26 }}>
                              <StreamState state={getStreamState(stream.id)} size={14} />
                            </ListItemIcon>
                            <ListItemText primary={title} primaryTypographyProps={{ variant: "body2" }} />
                          </ListItemButton>
                        );
                      })}
                    </List>
                  </Paper>
                </Box>
              </Stack>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseNameDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveName} disabled={!dialogDirty}>
            {nameDialogMode === "add" ? "Add" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={deleteDialogOpen} onClose={handleCloseDelete}>
        <DialogTitle>Remove page</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Remove the "{tabs[activeTab] ?? "page"}" tab? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDelete}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleConfirmDelete} disabled={tabs.length <= 1}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MosaicPage;
